/**
 * PDP offer maths (pdp_offer metaobjects, from snippets/pdp-offer-config.liquid), shared by the
 * buy box below and the Choose your bundle cards so both show the same prices. Prices are in pence.
 */
window.pdpOffers = {
    /**
     * The offer whose code is on the cart and applies, from pdp-discount-code.js's cart report.
     * Shopify only says a code applies once the cart holds something it covers, so a code it
     * marks as not applying is trusted until the offer's product is in the cart, then dropped.
     */
    pick(offers = [], cart = window.pdpOfferCart) {
        const codes = cart?.codes || [];
        return offers.find((offer) => codes.some((entry) =>
            String(entry.code).toUpperCase() === String(offer.code).toUpperCase()
            && (entry.applicable || !(cart.productIds || []).includes(Number(offer.productId)))
        )) || null;
    },

    // Until the cart report arrives, a page's own code is assumed to land, so its price doesn't flash.
    pickBeforeReport(offers, autoApplyCode) {
        const assumed = autoApplyCode ? { codes: [{ code: autoApplyCode, applicable: false }], productIds: [] } : undefined;
        return this.pick(offers, window.pdpOfferCart || assumed);
    },

    live(offer) {
        if (!offer) return false;

        const now = Date.now() / 1000;
        if (offer.startsAt && now < offer.startsAt) return false;
        if (offer.endsAt && now > offer.endsAt) return false;
        return true;
    },

    applies(offer, purchaseType) {
        if (!this.live(offer)) return false;

        const appliesTo = offer.appliesTo || 'both';
        return appliesTo === 'both'
            || appliesTo === (purchaseType === 'onetime' ? 'one_time' : 'subscription');
    },

    // Offer amounts are in the shop's currency (GBP); Shopify converts fixed amounts at this rate.
    // A market price (the offer's `valueIsLocal`) is already in the shopper's currency.
    fixedAmountInPence(value, local = false) {
        return Math.round(value * 100 * (local ? 1 : Number(window.Shopify?.currency?.rate) || 1));
    },

    /**
     * One unit's price after the offer. A code only covers the first subscription payment,
     * so `ongoing` keeps the regular subscription price. Types: `percentage` and `fixed_amount`
     * take the value off; `fixed_price` is the price itself (STARTERPACK: £39.98 subscribed or not).
     */
    price(offer, price, purchaseType, ongoing = false) {
        if (!price || !this.applies(offer, purchaseType)) return price;
        if (ongoing && purchaseType === 'subscription') return price;

        const value = Number(offer.value) || 0;
        const amount = this.fixedAmountInPence(value, offer.valueIsLocal);
        if (offer.type === 'fixed_price') return Math.min(price, amount);

        // Checkout rounds a percentage discount down to the penny (40% of £158.67 is £63.46 off).
        const discounted = offer.type === 'percentage'
            ? price - Math.floor((price * value) / 100)
            : price - amount;
        return Math.max(0, discounted);
    },

    format(price) {
        const hasPence = price % 100 !== 0;
        return new Intl.NumberFormat(window.currencyConfig.locale, {
            style: 'currency',
            currency: window.currencyConfig.currency,
            minimumFractionDigits: hasPence ? 2 : 0,
            maximumFractionDigits: 2,
        }).format(price / 100);
    },
};

document.addEventListener('alpine:init', () => {
    /**
     * The lowest price badge on the first gallery image (snippets/product-template-price-badge.liquid).
     * Liquid works out the cheapest one-time and subscription prices; this takes off the PDP offer
     * the buy box picked from the cart's codes, with the same maths as the buy box.
     */
    Alpine.data('pdpPriceBadge', ({ onetime = null, subscription = null } = {}) => {
        const listeners = new AbortController();
        const offers = window.pdpOffers;

        return {
            offer: null,

            init() {
                const refresh = () => {
                    this.offer = window.subscriptionPurchase?.offer ?? null;
                };
                refresh();
                document.addEventListener('pdp-offer:changed', refresh, { signal: listeners.signal });
            },

            destroy() {
                listeners.abort();
            },

            get price() {
                const prices = [
                    offers.price(this.offer, onetime, 'onetime'),
                    offers.price(this.offer, subscription, 'subscription'),
                ].filter((price) => typeof price === 'number');
                return prices.length ? Math.min(...prices) : null;
            },

            get text() {
                return this.price === null ? '' : offers.format(this.price);
            },
        };
    });

    Alpine.data('productTemplateInfo', (selectedFlavour) => {
        // Aborted in destroy(), so a buy box taken off the page (a closed quick shop) stops listening.
        const listeners = new AbortController();
        // What init() put on window.subscriptionPurchase; Alpine's `this` in destroy() is a different proxy.
        let instance = null;
        // Data is read from the buy box's own section, so a second buy box on the page
        // (the quick shop modal) doesn't pick up another product's config.
        const sectionOf = (el) => el?.closest?.('.shopify-section') || document;
        const readJson = (scope, selector, fallback) => JSON.parse(scope.querySelector(selector)?.textContent || fallback);
        const readPurchaseConfig = (root) => {
            const configEl = root?.querySelector?.('#subscription-purchase-config')
                || document.getElementById('subscription-purchase-config');
            return JSON.parse(configEl?.textContent || '{}');
        };
        const isConfigTrue = (value) => value === true || value === 'true';
        const readOfferConfig = (scope) => readJson(scope, '#pdp-offer-config', 'null');
        const cartUrl = () => `${window.Shopify?.routes?.root || '/'}cart.js`;
        const offers = window.pdpOffers;
        const fixedAmountInPence = (value, local) => offers.fixedAmountInPence(value, local);

        return {
            selectedFlavour,
            flavourData: {},
            servingData: {},
            selectedPurchaseType: 'subscription',
            selectedVariant: null,
            selectedVariantSubscriptionOptionIndex: null,
            availablePlans: [],
            selectedPlan: null,
            productData: null,
            variantMetafields: {},
            giftProductVariantsJson: [],
            rewardProductVariantsJson: [],
            includeOneTimePurchases: false,
            // Purchase types "Price layout: simple": the one-time price is the anchor for savings.
            simplePricing: false,
            // "Strike through the compare-at price": the compare-at price, where set, replaces that anchor.
            strikeCompareAt: false,
            stickySubscriptionButtonText: 'Buy Now',
            stickyOnetimeButtonText: 'Add to Cart',
            // Rewards-ladder A/B: blocks repeat ATC clicks until the add settles.
            isAddingToCart: false,
            // Outside the rewards lock: an add is running (see setOrderButtonsAdding).
            orderButtonsAdding: false,
            // The PDP offer (pdp_offer metaobject) whose code is on the cart.
            offer: null,

            applyPurchaseConfig(config = {}) {
                this.variantMetafields = config.variantMetafields || {};
                this.giftProductVariantsJson = config.giftProductVariantsJson || [];
                this.rewardProductVariantsJson = config.rewardProductVariantsJson || [];
                this.includeOneTimePurchases = isConfigTrue(config.includeOneTimePurchases);
                this.simplePricing = isConfigTrue(config.simplePricing);
                this.strikeCompareAt = isConfigTrue(config.strikeCompareAt);
                this.stickySubscriptionButtonText = config.stickySubscriptionButtonText || 'Buy Now';
                this.stickyOnetimeButtonText = config.stickyOnetimeButtonText || 'Add to Cart';
            },

            init() {
                const scope = sectionOf(this.$root);
                this.flavourData = readJson(scope, '#flavour-data', '{}');
                this.servingData = readJson(scope, '#serving-data', '{}');
                this.applyPurchaseConfig(readPurchaseConfig(this.$el));
                instance = this;
                window.subscriptionPurchase = this;
                this.watchOfferCode(scope);
                document.addEventListener('variant-changed', (event) => {
                    this.selectedVariant = event.detail.variant;
                }, { signal: listeners.signal });

                const productJson = this.$root.querySelector('.product-template__json');
                if (productJson) {
                    this.productData = JSON.parse(productJson.textContent);
                    // The server-selected variant (?variant=, Preselected variant ID), not just the first.
                    const selectedId = Number(this.$root.dataset.selectedVariantId);
                    this.selectedVariant = this.productData.variants.find((variant) => variant.id === selectedId)
                        || this.productData.variants[0];
                    this.updatePlans();
                }

                this.$watch('selectedVariant', () => {
                    this.updatePlans();
                });

                this.setupPurchaseTypeButtons();
            },

            destroy() {
                listeners.abort();
                if (window.subscriptionPurchase === instance) window.subscriptionPurchase = null;
            },

            getFlavourUseText() {
                return this.flavourData[this.selectedFlavour] || null;
            },

            /**
             * Shows the offer whose code is on the cart. The page-load script reports the
             * cart's codes; until it does, assume this template's own code lands so prices
             * don't flash back to full price.
             */
            watchOfferCode(scope = document) {
                const config = readOfferConfig(scope);
                if (!config?.offers?.length) return;

                const pickOffer = () => {
                    this.offer = offers.pickBeforeReport(config.offers, config.autoApplyCode);
                    document.dispatchEvent(new CustomEvent('pdp-offer:changed'));
                };

                pickOffer();
                document.addEventListener('pdp-offer:cart-codes', pickOffer, { signal: listeners.signal });
            },

            // The applied message split around [code], so the code can be set in bold.
            get offerAppliedText() {
                const text = this.offer?.appliedText || '';
                if (!text.includes('[code]')) return [`${text} `, ''];
                const [before, ...after] = text.split('[code]');
                return [before, after.join('[code]')];
            },

            get offerLive() {
                return offers.live(this.offer);
            },

            offerApplies(purchaseType = this.selectedPurchaseType) {
                return offers.applies(this.offer, purchaseType);
            },

            // One unit's price after the offer, in pence; `ongoing` keeps the regular subscription price.
            offerPrice(price, purchaseType = this.selectedPurchaseType, ongoing = false) {
                return offers.price(this.offer, price, purchaseType, ongoing);
            },

            get planPrice() {
                return this.offerPrice(this.selectedAllocation?.price, 'subscription');
            },

            // With the offer, strike through the price before the code; otherwise the compare-at price.
            get planStrikePrice() {
                const allocation = this.selectedAllocation;
                if (!allocation) return null;
                if (this.offerApplies('subscription')) return allocation.price;
                return allocation.compare_at_price > allocation.price ? allocation.compare_at_price : null;
            },

            get planThenText() {
                if (!this.selectedAllocation || !this.offerApplies('subscription')) return '';
                return `First delivery, then ${this.formatPrice(this.selectedAllocation.price)}`;
            },

            // Simple price layout's anchor: the one-time price, or the compare-at price with strikeCompareAt.
            get simpleAnchorPrice() {
                const variant = this.selectedVariant;
                if (!variant) return null;
                if (this.strikeCompareAt && variant.compare_at_price > variant.price) return variant.compare_at_price;
                return variant.price;
            },

            // Simple price layout: the anchor is the only strikethrough.
            get simpleStrikePrice() {
                const price = this.simpleAnchorPrice;
                return price > this.planPrice ? price : null;
            },

            // "You save £11.25 every box (20%)", or "on your first box" while an offer covers it.
            get planSavingText() {
                const anchor = this.simpleStrikePrice;
                if (!anchor) return '';

                const saving = anchor - this.planPrice;
                if (this.offerApplies('subscription')) {
                    return `You save ${this.formatPrice(saving)} on your first box`;
                }
                return `You save ${this.formatPrice(saving)} every box (${Math.round((saving / anchor) * 100)}%)`;
            },

            // "Every 4 weeks. Skip or cancel anytime.", from plan names like "Delivery every 4 weeks".
            get planRenewalText() {
                const allocation = this.selectedAllocation;
                if (!allocation) return '';

                const interval = allocation.selling_plan?.name?.match(/every\s.+$/i)?.[0].toLowerCase() || '';
                const skip = 'Skip or cancel anytime.';
                if (this.offerApplies('subscription')) {
                    return `Then ${this.formatPrice(allocation.price)}${interval ? ` ${interval}` : ''}. ${skip}`;
                }
                return interval ? `${interval.charAt(0).toUpperCase()}${interval.slice(1)}. ${skip}` : skip;
            },

            // The voucher badge beside a price, e.g. "−£5.50".
            offerSavingText(purchaseType) {
                const price = purchaseType === 'subscription'
                    ? this.selectedAllocation?.price
                    : this.selectedVariant?.price;
                if (!price || !this.offerApplies(purchaseType)) return '';
                return `−${this.formatPrice(price - this.offerPrice(price, purchaseType))}`;
            },

            get onetimePrice() {
                return this.offerPrice(this.selectedVariant?.price, 'onetime');
            },

            get onetimeStrikePrice() {
                const variant = this.selectedVariant;
                if (!variant) return null;
                if (this.offerApplies('onetime')) return this.simplePricing ? this.simpleAnchorPrice : variant.price;
                return variant.compare_at_price > variant.price ? variant.compare_at_price : null;
            },

            /**
             * Warns when the cart's discount for the offer's code differs from what the
             * page promised, e.g. the offer entry no longer matches the discount in admin.
             */
            async checkOfferInCart() {
                if (!this.offerLive || !this.productData) return;

                try {
                    const cart = await fetch(cartUrl(), { credentials: 'same-origin' }).then((r) => r.json());
                    const code = this.offer.code.toUpperCase();
                    const value = Number(this.offer.value) || 0;
                    const amount = fixedAmountInPence(value, this.offer.valueIsLocal);
                    let expected = 0;
                    let actual = 0;
                    let eligibleTotal = 0;
                    let eligibleLines = 0;

                    cart.items.forEach((item) => {
                        if (item.product_id !== this.productData.id) return;

                        actual += (item.line_level_discount_allocations || [])
                            .filter((allocation) => String(allocation.discount_application?.title).toUpperCase() === code)
                            .reduce((sum, allocation) => sum + allocation.amount, 0);

                        if (!this.offerApplies(item.selling_plan_allocation ? 'subscription' : 'onetime')) return;

                        eligibleLines += 1;
                        eligibleTotal += item.original_line_price;
                        if (this.offer.type === 'percentage') {
                            expected += Math.round(item.original_line_price * value / 100);
                        } else if (this.offer.type === 'fixed_price') {
                            // Once per order prices a single unit, the one with the biggest saving.
                            const unitOff = Math.max(0, item.original_price - amount);
                            expected = this.offer.fixedApplies === 'each_item'
                                ? expected + unitOff * item.quantity
                                : Math.max(expected, unitOff);
                        } else if (this.offer.fixedApplies === 'each_item') {
                            expected += Math.min(item.original_line_price, amount * item.quantity);
                        }
                    });

                    if (this.offer.type === 'fixed_amount' && this.offer.fixedApplies !== 'each_item') {
                        expected = Math.min(eligibleTotal, amount);
                    }

                    // Shopify rounds per line, so allow a penny per eligible line.
                    if (Math.abs(expected - actual) <= Math.max(1, eligibleLines)) return;

                    console.warn(
                        `PDP offer ${this.offer.code}: page expected ${expected}p off, cart has ${actual}p off.`
                    );
                    window.dataLayer?.push({
                        event: 'pdp_offer_mismatch',
                        offer_code: this.offer.code,
                        expected_discount: expected / 100,
                        cart_discount: actual / 100,
                    });
                } catch {
                    // The check is advisory; never block the add to cart.
                }
            },

            // Whole amounts read "£45"; anything with pence keeps both digits, e.g. "£39.50".
            formatPrice(price) {
                return offers.format(price);
            },

            getSizeEntry(sizeHandle) {
                return this.servingData[this.selectedFlavour]?.[sizeHandle];
            },

            getSizeEntryPrice(entry) {
                if (!entry) return null;
                if (this.selectedPurchaseType === 'subscription' && entry.selling_plan_price) {
                    return this.offerPrice(entry.selling_plan_price, 'subscription', true);
                }
                return this.offerPrice(entry.price ?? null, 'onetime');
            },

            getSizePricePerDay(sizeHandle) {
                const entry = this.getSizeEntry(sizeHandle);
                const price = this.getSizeEntryPrice(entry);
                if (!price || !entry?.size_in_ml) return '';
                return this.formatPrice(price / entry.size_in_ml);
            },

            getSizeTotalPrice(sizeHandle) {
                const entry = this.getSizeEntry(sizeHandle);
                const price = this.getSizeEntryPrice(entry);
                if (!price) return '';
                return `${this.formatPrice(price)} total`;
            },

            get selectedAllocation() {
                return (
                    this.availablePlans.find((a) => a.selling_plan?.id == this.selectedPlan) ?? null
                );
            },

            get sellingPlanAllocationDiscountPercentage() {
                const allocation = this.selectedAllocation;
                if (!allocation?.compare_at_price || allocation.compare_at_price <= allocation.price) {
                    return '';
                }

                return Math.round(
                    ((allocation.compare_at_price - allocation.price) / allocation.compare_at_price) * 100
                );
            },

            get discountBadgeText() {
                const discount = this.sellingPlanAllocationDiscountPercentage;
                return discount ? `+ ${discount}% OFF` : '';
            },

            // The selected option's price after any offer, in pence.
            get orderButtonPrice() {
                if (!this.selectedVariant) return null;

                const price =
                    this.selectedPurchaseType === 'subscription' && this.selectedAllocation
                        ? this.selectedAllocation.price
                        : this.selectedVariant.price;

                return price ? this.offerPrice(price) : null;
            },

            get orderButtonText() {
                if (this.isAddingToCart) return 'Adding to Cart...';

                const price = this.orderButtonPrice;
                return price ? `Add to Cart – ${this.formatPrice(price)}` : 'Add to Cart';
            },

            // Simple layout with an offer: the anchor, struck through after the button price.
            get orderButtonStrikePrice() {
                if (!this.simplePricing || this.isAddingToCart) return null;
                if (!this.offerApplies(this.selectedPurchaseType)) return null;

                const anchor = this.simpleAnchorPrice;
                return anchor > this.orderButtonPrice ? anchor : null;
            },

            // "Adding to Cart..." while an add runs outside the rewards lock. Plain-text buttons are set
            // directly; buttons rendered as markup (the struck price) follow orderButtonsAdding instead.
            setOrderButtonsAdding(adding) {
                this.orderButtonsAdding = adding;
                document.querySelectorAll('.onetime-button').forEach((button) => {
                    if (button.firstElementChild) return;
                    button.textContent = adding ? 'Adding to Cart...' : this.orderButtonText;
                });
            },

            setPricePerServing(price) {
                if (!price || !this.selectedVariant) return;

                const selectedVariantSizeInMl = this.variantMetafields[this.selectedVariant.id]?.size_in_ml;
                if (!selectedVariantSizeInMl) return;

                const perServing = price / selectedVariantSizeInMl;
                return `${this.formatPrice(perServing)} / serving`;
            },

            setupPurchaseTypeButtons() {
                document.addEventListener('click', (event) => {
                    const button = event.target.closest('.purchase-type__button');
                    if (!button || !this.$root.contains(button)) return;

                    const purchaseTypeContainer = button.closest('.product-template__purchase-type');
                    const purchaseType = purchaseTypeContainer.dataset.typeHandle;

                    if (purchaseType === 'subscription') {
                        this.selectedPurchaseType = 'subscription';
                        document.dispatchEvent(
                            new CustomEvent('purchase-type-changed', {
                                detail: {
                                    type: 'subscription',
                                    buttonText: this.stickySubscriptionButtonText,
                                    Price: this.availablePlans[0]
                                        ? this.formatPrice(this.offerPrice(this.availablePlans[0].price, 'subscription'))
                                        : '',
                                },
                            })
                        );
                    } else if (purchaseType === 'onetime') {
                        this.selectedPurchaseType = 'onetime';
                        document.dispatchEvent(
                            new CustomEvent('purchase-type-changed', {
                                detail: {
                                    type: 'onetime',
                                    buttonText: this.stickyOnetimeButtonText,
                                    Price: this.formatPrice(this.offerPrice(this.selectedVariant.price, 'onetime')),
                                },
                            })
                        );
                    }
                }, { signal: listeners.signal });
            },

            updatePlans() {
                if (!this.selectedVariant || !this.productData) return;

                const variant = this.productData.variants.find((v) => v.id === this.selectedVariant.id);
                const allocations = variant ? variant.selling_plan_allocations || [] : [];

                this.availablePlans = allocations
                    .map((allocation) => {
                        const group = this.productData.selling_plan_groups.find(
                            (g) => g.id === allocation.selling_plan_group_id
                        );
                        const sellingPlan = group
                            ? group.selling_plans.find((p) => p.id === allocation.selling_plan_id)
                            : null;

                        return { ...allocation, selling_plan: sellingPlan };
                    })
                    .filter((a) => a.selling_plan);

                this.selectedPlan =
                    this.availablePlans.length > 0 ? parseInt(this.availablePlans[0].selling_plan.id) : null;
            },

            selectPlan(planId) {
                this.selectedPlan = parseInt(planId);
            },

            isRewardsExperimentVariant() {
                return window.__igRewardsVariant === true
                    || document.documentElement.classList.contains('ab-variant')
                    || document.body.classList.contains('ab-variant');
            },

            getGiftChildItems() {
                const seen = new Set();
                const items = [];

                this.giftProductVariantsJson.forEach((item) => {
                    if (!item?.id || seen.has(item.id)) return;
                    seen.add(item.id);
                    items.push(item);
                });

                return items;
            },

            getSubscriptionChildItems() {
                const seen = new Set();
                const items = [];

                // Reward items are gated behind the Intelligems A/B test variation.
                const rewardItems = this.isRewardsExperimentVariant() ? this.rewardProductVariantsJson : [];

                [...this.giftProductVariantsJson, ...rewardItems].forEach((item) => {
                    if (!item?.id || seen.has(item.id)) return;
                    seen.add(item.id);
                    items.push(item);
                });

                return items;
            },

            /**
             * The cart can't read template settings, so the effective
             * one-time gift setting travels with the parent cart line.
             *
             * @returns {Object|null}
             */
            getParentLineProperties() {
                return this.includeOneTimePurchases
                    ? { _gifts_include_one_time: true }
                    : null;
            },

            buildGiftBundlePayload(sellingPlanId, childItems) {
                const groupId = `bundle_${Date.now()}`;
                const giftItems = childItems.map((item) => ({
                    id: item.id,
                    quantity: item.quantity,
                    properties: {
                        ...item.properties,
                        _free_gift_parent_variant_id: this.selectedVariant.id,
                        _bundle_group_id: groupId,
                        _is_child_product: true,
                    },
                }));

                const mainItem = {
                    id: this.selectedVariant.id,
                    quantity: 1,
                    properties: {
                        ...this.getParentLineProperties(),
                        _is_gift_parent: true,
                        _bundle_group_id: groupId,
                    },
                };

                if (sellingPlanId) {
                    mainItem.selling_plan = sellingPlanId;
                }

                return [mainItem, ...giftItems];
            },

            /**
             * Rewards-ladder A/B: gifts belong to the product, not the cart line,
             * so a product that already carries its gifts must not receive a
             * second set when another (or the same) variant is added.
             *
             * @param {Object} cart - The Carbon cart module.
             * @returns {Promise<boolean>}
             */
            async hasGiftsInCartForProduct(cart) {
                if (!this.isRewardsExperimentVariant() && !this.includeOneTimePurchases) {
                    return false;
                }
                if (typeof cart?.hasBundleChildItemsForProduct !== 'function') return false;

                return cart.hasBundleChildItemsForProduct(this.productData?.id);
            },

            /**
             * Rewards-ladder A/B: lock ATC buttons so rapid/simultaneous clicks
             * cannot enqueue multiple gift bundles before the first add settles.
             *
             * @param {boolean} isPending - Whether an add-to-cart is in flight.
             */
            setAddToCartPending(isPending) {
                if (!this.isRewardsExperimentVariant()) return;

                this.isAddingToCart = isPending;

                document
                    .querySelectorAll('.onetime-button, .sticky-atc__button')
                    .forEach((button) => {
                        if (isPending) {
                            button.setAttribute('disabled', 'disabled');
                            button.setAttribute('aria-busy', 'true');
                            return;
                        }

                        button.removeAttribute('aria-busy');

                        if (button.classList.contains('onetime-button') && !this.selectedVariant) {
                            button.setAttribute('disabled', 'disabled');
                            return;
                        }

                        button.removeAttribute('disabled');
                    });
            },

            /**
             * Opens the cart aside drawer after a successful add.
             */
            openCartAside() {
                setTimeout(() => {
                    const $cartAsideBackground = document.querySelector('.cart-aside__background');
                    if (!$cartAsideBackground) return;

                    const $cartAside = $cartAsideBackground.querySelector('aside.cart-aside');
                    if (!$cartAside) return;

                    document.querySelector('body').classList.add('overflow-y-hidden');
                    $cartAsideBackground.classList.remove('pointer-events-none', 'opacity-0');
                    $cartAsideBackground.classList.add(
                        'bg-white',
                        'bg-opacity-20',
                        'opacity-100',
                        'cart--is-open'
                    );
                    $cartAsideBackground.style.backdropFilter = 'blur(20px)';
                    $cartAside.classList.remove(
                        'translate-x-full',
                        'ponter-events-none',
                        'hidden'
                    );
                }, 100);
            },

            /**
             * Handles post-add cart UI (aside, AMP cart, or cart page redirect).
             *
             * @param {Object|null} response - Cart add response.
             * @param {Object} cart - The Carbon cart module.
             */
            handleAddToCartSuccess(response, cart) {
                if (!response) return;

                // Lets a quick shop modal close before the cart opens.
                this.$root.dispatchEvent(new CustomEvent('product:added-to-cart', { bubbles: true }));

                if (cart.useCartAside) {
                    this.openCartAside();
                    return;
                }

                if (window.AMP_API?.OPEN_CART) {
                    window.AMP_API.OPEN_CART();
                    return;
                }

                window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
            },

            addToCartBySelectedType() {
                if (this.isRewardsExperimentVariant() && this.isAddingToCart) return;

                if (this.selectedPurchaseType === 'subscription') {
                    this.addToCartWithSellingPlan();
                    return;
                }
                this.addToCartOneTime();
            },

            async addToCartWithSellingPlan() {
                const useRewardsLock = this.isRewardsExperimentVariant();

                if (useRewardsLock) {
                    if (this.isAddingToCart) return;
                    this.setAddToCartPending(true);
                } else {
                    this.setOrderButtonsAdding(true);
                }

                if (!this.selectedPlan || !this.selectedVariant) {
                    if (useRewardsLock) this.setAddToCartPending(false);
                    return;
                }

                const product = window.Carbon.Product;
                if (product?.addToCart) {
                    const tempButton = document.createElement('button');
                    tempButton.setAttribute('data-variant-id', this.selectedVariant.id);
                    tempButton.setAttribute('data-selling-plan-id', this.selectedPlan);
                    tempButton.setAttribute('data-quantity', '1');
                    product.addToCart(tempButton);
                    if (useRewardsLock) this.setAddToCartPending(false);
                    return;
                }

                const cart = window.Carbon.Cart;

                try {
                    const childItems = (await this.hasGiftsInCartForProduct(cart))
                        ? []
                        : this.getSubscriptionChildItems();

                    let response;

                    if (childItems.length > 0) {
                        response = await cart.addItemToCart(
                            this.buildGiftBundlePayload(this.selectedPlan, childItems)
                        );
                    } else {
                        response = await cart.addItemToCart(
                            this.selectedVariant.id,
                            1,
                            this.selectedPlan,
                            this.getParentLineProperties()
                        );
                    }

                    this.handleAddToCartSuccess(response, cart);
                    if (response) this.checkOfferInCart();

                    if (!useRewardsLock && cart.useCartAside) this.setOrderButtonsAdding(false);
                } finally {
                    if (useRewardsLock) this.setAddToCartPending(false);
                }
            },

            async addToCartOneTime() {
                if (!this.selectedVariant) return;

                const useRewardsLock = this.isRewardsExperimentVariant();

                if (useRewardsLock) {
                    if (this.isAddingToCart) return;
                    this.setAddToCartPending(true);
                }

                const product = window.Carbon.Product;
                if (product?.addToCart) {
                    const tempButton = document.createElement('button');
                    tempButton.setAttribute('data-variant-id', this.selectedVariant.id);
                    tempButton.setAttribute('data-quantity', '1');
                    product.addToCart(tempButton);
                    if (useRewardsLock) this.setAddToCartPending(false);
                    return;
                }

                const cart = window.Carbon.Cart;

                if (!useRewardsLock) this.setOrderButtonsAdding(true);

                try {
                    const childItems = this.includeOneTimePurchases
                        && !(await this.hasGiftsInCartForProduct(cart))
                        ? this.getGiftChildItems()
                        : [];

                    const response = childItems.length > 0
                        ? await cart.addItemToCart(
                            this.buildGiftBundlePayload(null, childItems)
                        )
                        : await cart.addItemToCart(
                            this.selectedVariant.id,
                            1,
                            null,
                            this.getParentLineProperties()
                        );
                    this.handleAddToCartSuccess(response, cart);
                    if (response) this.checkOfferInCart();

                    if (!useRewardsLock && cart.useCartAside) this.setOrderButtonsAdding(false);
                } finally {
                    if (useRewardsLock) this.setAddToCartPending(false);
                }
            },
        };
    });
});
